import { useState, useEffect, useRef } from 'react'
import { Check, ChevronsUpDown, Edit2, Trash2, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  fetchCustomerTypes,
  createCustomerType,
  updateCustomerType,
  softDeleteCustomerType,
  getBranchCountByType,
} from '../supabase-client'

export default function CustomerTypeComboBox({ value, onChange, disabled = false, error = false }) {
  const [open, setOpen] = useState(false)
  const [customerTypes, setCustomerTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingTypeId, setEditingTypeId] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  const [originalTypeName, setOriginalTypeName] = useState('')
  const [hoveredTypeId, setHoveredTypeId] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [customerCount, setCustomerCount] = useState(0)
  const [deleteTypeId, setDeleteTypeId] = useState(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const editInputRef = useRef(null)
  const newTypeInputRef = useRef(null)

  // Load customer types on mount
  useEffect(() => {
    loadCustomerTypes()
  }, [])

  // Focus edit input when entering edit mode
  useEffect(() => {
    if (editingTypeId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingTypeId])

  // Focus new type input when entering create mode
  useEffect(() => {
    if (isCreatingNew && newTypeInputRef.current) {
      newTypeInputRef.current.focus()
    }
  }, [isCreatingNew])

  const loadCustomerTypes = async () => {
    try {
      setLoading(true)
      const types = await fetchCustomerTypes(true)
      setCustomerTypes(types)
    } catch (error) {
      console.error('Failed to load customer types:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSelectedTypeName = () => {
    if (!value) return 'Select customer type...'
    const selectedType = customerTypes.find(type => type.id === value)
    return selectedType ? selectedType.name : 'Select customer type...'
  }

  const handleSelectType = (typeId) => {
    onChange(typeId)
    setOpen(false)
  }

  const handleStartEdit = (typeId, typeName, e) => {
    e.stopPropagation()
    setEditingTypeId(typeId)
    setEditingValue(typeName)
    setOriginalTypeName(typeName)
  }

  const handleEditBlur = async () => {
    if (!editingTypeId) return

    const trimmedValue = editingValue.trim()

    // Handle deletion via backspace (empty value)
    if (trimmedValue === '' && originalTypeName !== '') {
      const count = await getBranchCountByType(editingTypeId)
      setCustomerCount(count)
      setDeleteTypeId(editingTypeId)
      setShowDeleteDialog(true)
      return
    }

    // Handle normal save
    if (trimmedValue !== '' && trimmedValue !== originalTypeName) {
      try {
        await updateCustomerType(editingTypeId, { name: trimmedValue })
        await loadCustomerTypes()
      } catch (error) {
        console.error('Failed to update customer type:', error)
        alert(error.message || 'Failed to update customer type')
        // Restore original value on error
        setEditingValue(originalTypeName)
      }
    }

    // Exit edit mode
    setEditingTypeId(null)
    setOriginalTypeName('')
  }

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEditBlur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // Restore original value and exit edit mode
      setEditingValue(originalTypeName)
      setEditingTypeId(null)
      setOriginalTypeName('')
    }
  }

  const handleDeleteClick = async (typeId, e) => {
    e.stopPropagation()
    const count = await getBranchCountByType(typeId)
    setCustomerCount(count)
    setDeleteTypeId(typeId)
    setShowDeleteDialog(true)
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)

    // If deletion triggered from backspace pattern, restore original text
    if (editingTypeId === deleteTypeId) {
      setEditingValue(originalTypeName)
      // Return focus to input for continued editing
      if (editInputRef.current) {
        setTimeout(() => {
          editInputRef.current.focus()
        }, 100)
      }
    }

    setDeleteTypeId(null)
    setCustomerCount(0)
  }

  const handleDeleteProceed = async () => {
    try {
      await softDeleteCustomerType(deleteTypeId)
      await loadCustomerTypes()

      // If deleted type was selected, clear the selection
      if (value === deleteTypeId) {
        onChange(null)
      }

      setShowDeleteDialog(false)
      setDeleteTypeId(null)
      setCustomerCount(0)
      setEditingTypeId(null)
      setOriginalTypeName('')
    } catch (error) {
      console.error('Failed to delete customer type:', error)
      alert(error.message || 'Failed to delete customer type')

      // Restore text on error if from backspace pattern
      if (editingTypeId === deleteTypeId) {
        setEditingValue(originalTypeName)
      }

      setShowDeleteDialog(false)
    }
  }

  const handleAddNewType = () => {
    setIsCreatingNew(true)
    setNewTypeName('')
  }

  const handleCreateNewType = async () => {
    const trimmedName = newTypeName.trim()

    if (trimmedName === '') {
      alert('Customer type name cannot be empty')
      return
    }

    try {
      const newType = await createCustomerType({
        name: trimmedName,
        display_order: customerTypes.length + 1
      })

      await loadCustomerTypes()

      // Auto-select the newly created type
      onChange(newType.id)

      // Close the combo box
      setOpen(false)
      setIsCreatingNew(false)
      setNewTypeName('')
    } catch (error) {
      console.error('Failed to create customer type:', error)
      alert(error.message || 'Failed to create customer type')
    }
  }

  const handleNewTypeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreateNewType()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsCreatingNew(false)
      setNewTypeName('')
    }
  }

  const handleNewTypeBlur = () => {
    // Don't auto-create on blur, just cancel
    setIsCreatingNew(false)
    setNewTypeName('')
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn("w-full justify-between bg-white hover:bg-gray-50 transition-colors", error && "border-red-500")}
          >
            <span className={cn(!value && "text-gray-500")}>
              {getSelectedTypeName()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search customer types..." />
            <CommandList>
              <CommandEmpty>No customer type found.</CommandEmpty>
              <CommandGroup>
                {loading ? (
                  <div className="py-6 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : (
                  <>
                    {customerTypes.map((type) => (
                      <CommandItem
                        key={type.id}
                        value={type.name}
                        onSelect={() => !editingTypeId && handleSelectType(type.id)}
                        onMouseEnter={() => setHoveredTypeId(type.id)}
                        onMouseLeave={() => setHoveredTypeId(null)}
                        className="relative group"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === type.id ? "opacity-100" : "opacity-0"
                          )}
                        />

                        {editingTypeId === type.id ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={handleEditBlur}
                            onKeyDown={handleEditKeyDown}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        ) : (
                          <span className="flex-1">{type.name}</span>
                        )}

                        {hoveredTypeId === type.id && editingTypeId !== type.id && (
                          <div className="ml-auto flex items-center gap-1">
                            <button
                              onClick={(e) => handleStartEdit(type.id, type.name, e)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              <Edit2 className="h-3 w-3 text-gray-600" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(type.id, e)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </button>
                          </div>
                        )}
                      </CommandItem>
                    ))}

                    {/* Add New Customer Type */}
                    {isCreatingNew ? (
                      <div className="px-2 py-1.5 flex items-center gap-2">
                        <Plus className="h-4 w-4 text-green-600" />
                        <input
                          ref={newTypeInputRef}
                          type="text"
                          value={newTypeName}
                          onChange={(e) => setNewTypeName(e.target.value)}
                          onBlur={handleNewTypeBlur}
                          onKeyDown={handleNewTypeKeyDown}
                          placeholder="Enter type name..."
                          className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={handleAddNewType}
                        className="w-full px-2 py-1.5 flex items-center gap-2 text-green-600 hover:bg-gray-100 rounded-sm transition-colors text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add new customer type</span>
                      </button>
                    )}
                  </>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer Type?</AlertDialogTitle>
            <AlertDialogDescription>
              Warning: You are about to delete this customer type.
              {customerCount > 0 ? (
                <span className="font-medium">
                  {' '}This will affect {customerCount} customer{customerCount !== 1 ? 's' : ''}
                </span>
              ) : (
                <span> This will affect 0 customers</span>
              )} and cannot be undone. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel} autoFocus>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProceed}
              variant="destructive"
            >
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
